<template>
  <div>
    <PageTitle title="Room Reservation Details" :backBtn="true" />

    <v-container fluid>
      <v-row>
        <v-col cols="12" lg="4">
          <v-card class="lighten-12" height="100%">
            <v-card-title>General</v-card-title>
            <v-container fluid>
              <v-row class="detail-row">
                <v-col cols="6"><h4 class="detail-label">Reference number</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ reservationDetails.reference_number || "---------" }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Date</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ reservationDetails.date ? reservationDetails.date : "---------" | formatDate }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Vehicle number</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ reservationDetails.vehicle_number || "---------" }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Total amount</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ text(reservationDetails.total_amount) }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Paid amount</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ text(reservationDetails.paid_amount) }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Total order amount</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ text(reservationDetails.order_total_amount) }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Total room amount</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ text(reservationDetails.room_total_amount) }}</v-chip></v-col>

                <v-col cols="6"><h4 class="detail-label">Status</h4></v-col>
                <v-col cols="6"><v-chip class="detail-value" label>{{ reservationDetails.status || "---------" }}</v-chip></v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
        <v-col cols="12" lg="8">
          <v-card class="lighten-12" height="100%">
            <v-card-title>Guest</v-card-title>
            <v-container fluid>
              <v-row class="detail-row">
                <v-col cols="6" md="3"><h4 class="detail-label">Guest name</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.name || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">NIC number</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.nic_number || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">Passport number</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.passport_number || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">Phone number</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.phone || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">E-mail</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.email || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">Address</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.address || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">Company</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.company || "-----" }}</v-chip></v-col>

                <v-col cols="6" md="3"><h4 class="detail-label">Date of birth</h4></v-col>
                <v-col cols="6" md="3"><v-chip class="detail-value" label>{{ guest.date_of_birth ? guest.date_of_birth : "-----" | formatDate }}</v-chip></v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="lighten-12">
            <v-card-title>Orders</v-card-title>
            <v-container fluid>
              <v-data-table
                :fixed-header="true"
                show-expand
                :headers="headers"
                :items="reservationDetails.orders"
                single-expand
                :filter="filter"
                hide-default-footer
                item-key="order_number"
              >
                <template v-slot:item.date="{ item }">
                  {{ item.date | formatDate }}
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="px-5">
                    <template v-if="item.items">
                      <v-card class="pa-0 mt-2 mb-2">
                        <v-data-table
                          single-expand="'true'"
                          hide-default-footer="true"
                          :headers="subheaders"
                          :items="item.items"
                        ></v-data-table>
                      </v-card>
                    </template>
                  </td>
                </template>
              </v-data-table>
            </v-container>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="lighten-12">
            <v-card-title>Room types</v-card-title>

            <v-container fluid>
              <v-data-table
                :fixed-header="true"
                :headers="columns"
                :items="reservationDetails.rooms"
                :filter="filter"
                hide-default-footer
                item-key="name"
              >
                <template v-slot:item.check_in="{ item }">
                  {{ item.check_in | formatDate }}
                </template>
                <template v-slot:item.check_out="{ item }">
                  {{ item.check_out | formatDate }}
                </template>
              </v-data-table>
            </v-container>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="lighten-12">
            <v-card-title>History</v-card-title>
            <v-container fluid>
              <v-data-table
                :headers="historyHeaders"
                :items="history"
                :loading="historyLoading"
                item-key="id"
                :items-per-page="10"
              >
                <template v-slot:item.created_at="{ item }">
                  {{ formatWhen(item.created_at) }}
                </template>
                <template v-slot:no-data>
                  <div class="py-4 text-center">No history yet</div>
                </template>
              </v-data-table>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import moment from "moment";

export default {
  data: () => ({
    reservationDetails: [],
    history: [],
    historyLoading: false,
    expanded: [],
    historyHeaders: [
      { text: "Date and time", value: "created_at", width: "18%" },
      { text: "Change", value: "field_label", width: "18%" },
      { text: "Details", value: "summary" },
      { text: "From", value: "old_value", width: "20%" },
      { text: "To", value: "new_value", width: "20%" },
    ],

    columns: [
      {
        text: "Reference number",
        value: "room.reference_number",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "left",
      },
      {
        text: "Check-In",
        value: "check_in",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "left",
      },
      {
        text: "Check-out",
        value: "check_out",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "left",
      },
      {
        text: "Adults",
        value: "adults",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "center",
      },
      {
        text: "Childs",
        value: "child",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "center",
      },
      {
        text: "Room Type",
        value: "roomType.name",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "center",
      },
      {
        text: "Room Type Code",
        value: "roomType.code",
        show: true,
        disabled: false,
        width: "12.5%",
        align: "center",
      },
      {
        text: "Amount",
        value: "amount",
        show: true,
        disabled: false,
        width: "10%",
        align: "center",
      },
    ],
    headers: [
      {
        text: "Order number",
        value: "order_number",
        show: true,
        disabled: false,

        align: "left",
      },
      {
        text: "Date",
        value: "date",
        show: true,
        disabled: false,

        align: "left",
      },
      {
        text: "Total Amount",
        value: "total_amount",
        show: true,
        disabled: false,

        align: "left",
      },
      {
        text: "Room name",
        value: "room.name",
        show: true,
        disabled: false,

        align: "center",
      },
      {
        text: "Room referance number",
        value: "room.reference_number",
        show: true,
        disabled: false,

        align: "center",
      },
    ],
    subheaders: [
      {
        text: "Name",
        value: "dish.name",
        show: true,
        disabled: false,

        align: "center",
      },
      {
        text: "Dish Type",
        value: "unit.name",
        show: true,
        disabled: false,

        align: "center",
      },
      {
        text: "Quantity",
        value: "quantity",
        show: true,
        disabled: false,

        align: "center",
      },
      {
        text: "Price",
        value: "amount",
        show: true,
        disabled: false,

        align: "center",
      },
    ],
  }),
  expandRow(item) {
    // would be
    // this.expanded = [item]
    // but if you click an expanded row, you want it to collapse
    this.expanded = item === this.expanded[0] ? [] : [item];
  },
  computed: {
    guest() {
      const details = this.reservationDetails;
      return details && details.guest ? details.guest : {};
    },
  },
  methods: {
    text(value, empty = "---------") {
      return value === null || value === undefined || value === "" ? empty : value;
    },
    formatWhen(value) {
      if (!value) return "—";
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : value;
    },
    getPurchaseOrderStatusColor(status) {},
    GetReservationDetails() {
      let Id = this.$route.params.id;
      axios
        .get(`hms/reservations/${Id}`)
        .then((res) => {
          this.reservationDetails = res.data;
        })
        .catch((err) => {});
      this.historyLoading = true;
      axios
        .get(`hms/reservations/${Id}/history`)
        .then((res) => {
          this.history = Array.isArray(res.data) ? res.data : [];
          this.historyLoading = false;
        })
        .catch(() => {
          this.history = [];
          this.historyLoading = false;
        });
    },
  },
  created() {
    this.GetReservationDetails();
  },
};
</script>

<style scoped>
.detail-row {
  align-items: center;
}

.detail-label {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.detail-value {
  height: auto;
  max-width: 100%;
  white-space: normal;
}

.detail-value >>> .v-chip__content {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
